import React from 'react';
import styled from 'styled-components';
import { clients, featured, industries } from './renewalData';
import {
  Block,
  BlockTitle,
  Eyebrow,
  HairlineGrid,
  Inner,
  Lead,
  SectionHead,
  SectionNo,
  SectionShell,
  SectionTitle,
  token,
} from './renewalStyles';

const FeaturedCard = styled.article`
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: background 0.3s;

  &:hover {
    background: #fff5f5;
  }

  .thumb {
    aspect-ratio: 16 / 10;
    overflow: hidden;
    border-bottom: 1px solid ${token.border};
    background: #eee;
  }

  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.7s ${token.ease};
  }

  &:hover .thumb img {
    transform: scale(1.07);
  }

  .body {
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
  }

  .client {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: ${token.primary};
  }

  .title {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.3;
  }

  .desc {
    margin: 0;
    font-size: 15px;
    line-height: 1.75;
    color: ${token.muted};
    white-space: pre-line;
    flex: 1;
  }

  .link {
    font-size: 13px;
    font-weight: 600;
    word-break: break-all;
  }
`;

const IndustryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
`;

const IndustryName = styled.h4`
  margin: 0 0 24px;
  font-size: clamp(20px, 2.2vw, 28px);
  font-weight: 800;
  border-bottom: 2px solid #000;
  padding-bottom: 14px;
`;

const IndustryItem = styled.div`
  background: #fff;
  padding: 26px 24px;

  .title {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.45;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.7;
    color: ${token.muted};
    white-space: pre-line;
  }
`;

const ClientGrid = styled.div`
  background: #fff;
  border: 2px solid #000;
  padding: clamp(28px, 4vw, 56px);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(160px, 100%), 1fr));
  gap: clamp(24px, 3vw, 44px);
  align-items: center;
  justify-items: center;

  img {
    max-width: 140px;
    max-height: 44px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: grayscale(1);
    opacity: 0.7;
    transition: filter 0.4s, opacity 0.4s, transform 0.4s;
  }

  img:hover {
    filter: grayscale(0);
    opacity: 1;
    transform: scale(1.08);
  }
`;

export default function Portfolios() {
  return (
    <SectionShell id="portfolios" $bg={token.surface}>
      <Inner>
        <SectionHead $gap="24px">
          <SectionNo>04</SectionNo>
          <SectionTitle>PORTFOLIOS</SectionTitle>
        </SectionHead>
        <Lead>
          BRIT는 제조 및 자동차, 에너지, 금융, 공공기관, IT 등 전 분야에 걸쳐 전문성을 바탕으로 프로젝트를 수행합니다.
        </Lead>

        <Block>
          <Eyebrow>FEATURED PORTFOLIOS</Eyebrow>
          <BlockTitle>주요 진행 프로젝트</BlockTitle>
          <HairlineGrid $min="360px">
            {featured.map(item => (
              <FeaturedCard key={item.title} data-reveal="true">
                <div className="thumb">
                  <img src={item.img} alt={item.alt} loading="lazy" />
                </div>
                <div className="body">
                  <div className="client">{item.client}</div>
                  <div className="title">{item.title}</div>
                  <p className="desc">{item.desc}</p>
                  {item.url && (
                    <a className="link" href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.urlLabel} ↗
                    </a>
                  )}
                </div>
              </FeaturedCard>
            ))}
          </HairlineGrid>
        </Block>

        <Block>
          <Eyebrow>PORTFOLIOS LIST</Eyebrow>
          <BlockTitle>산업별 수행 프로젝트 리스트</BlockTitle>
          <IndustryList>
            {industries.map(group => (
              <div key={group.name} data-reveal="true">
                <IndustryName>{group.name}</IndustryName>
                <HairlineGrid $min="320px">
                  {group.items.map(item => (
                    <IndustryItem key={item.title}>
                      <div className="title">{item.title}</div>
                      <p>{item.desc}</p>
                    </IndustryItem>
                  ))}
                </HairlineGrid>
              </div>
            ))}
          </IndustryList>
        </Block>

        <Block $mb="0">
          <Eyebrow>FEATURED CLIENTS</Eyebrow>
          <BlockTitle>주요 고객사</BlockTitle>
          <ClientGrid>
            {clients.map(client => (
              <img key={client.name} src={client.src} alt={client.name} loading="lazy" />
            ))}
          </ClientGrid>
        </Block>
      </Inner>
    </SectionShell>
  );
}
